import React from 'react';
import { Box, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Spinner } from 'src/components/atoms';
import { ANSWER_TYPES, QuestionData } from 'src/types/formQuestionnaire';
import { PreviewAction, QuestionAnswer } from './FormPreview.reducer';
import {
  ButtonWrapper,
  EmptyStateContainer,
  EmptyStateImage,
  EmptyStateText,
  FormPreviewCard,
  NavButton,
  PreviewMainSection,
  ProgressBarRow,
  ProgressBarWrapper,
  ProgressSection,
  ProgressText,
  QuestionContentInner,
  QuestionContentSection,
  QuestionDescription,
  QuestionTextSection,
  QuestionTitle,
  TranslatingOverlay,
  TranslatingOverlayCopy,
  TranslatingOverlayStack,
} from './FormPreview.styles';
import { PREVIEW_SCREENS } from 'src/types/formQuestionnaire';
import { PreviewRichText } from './PreviewRichText';

const RICH_TEXT_TAGS =
  /<(ol|ul|li|h[1-6]|blockquote|strong|em|u|s|a|img|pre|code|sub|sup)\b/i;
const hasRichTextFormatting = (html: string): boolean =>
  RICH_TEXT_TAGS.test(html);
import { QuestionInput } from './QuestionInput';
import { FormPreviewInlinePlacement, PreviewQuestionMinimal } from './types';

interface FormPreviewCardContentProps {
  readOnly: boolean;
  isTranslating: boolean;
  inlinePlacement: FormPreviewInlinePlacement;
  dataTestId: string;
  sortedQuestions: PreviewQuestionMinimal[] | QuestionData[];
  readOnlyStep: number;
  currentQuestionReadOnly: PreviewQuestionMinimal | null;
  currentQuestionInteractive: QuestionData | null;
  screen: string;
  answers: Record<string, QuestionAnswer>;
  dispatch: React.Dispatch<PreviewAction>;
  fileInputId: string;
  emptyStateSrc: string;
  hasQuestions: boolean;
  progressReadOnly: number;
  progressInteractive: number;
  showBackButton: boolean;
  showNextButton: boolean;
  questionsCountInteractive: number;
  currentQuestionPositionInteractive: number;
  handleBackReadOnly: () => void;
  handleNextReadOnly: () => void;
  handlers: {
    handleBack: () => void;
    handleNext: () => void;
    handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleDeleteFile: (fileId: string) => void;
    handleFileDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  };
  textInputPlaceholder: string;
  dropdownPlaceholder: string;
  databaseFieldLabel: string;
  databaseFieldPlaceholder: string;
}

export const FormPreviewCardContent: React.FC<FormPreviewCardContentProps> = ({
  readOnly,
  isTranslating,
  inlinePlacement,
  dataTestId,
  sortedQuestions,
  readOnlyStep,
  currentQuestionReadOnly,
  currentQuestionInteractive,
  screen,
  answers,
  dispatch,
  fileInputId,
  emptyStateSrc,
  hasQuestions,
  progressReadOnly,
  progressInteractive,
  showBackButton,
  showNextButton,
  questionsCountInteractive,
  currentQuestionPositionInteractive,
  handleBackReadOnly,
  handleNextReadOnly,
  handlers,
  textInputPlaceholder,
  dropdownPlaceholder,
  databaseFieldLabel,
  databaseFieldPlaceholder,
}) => (
  <PreviewMainSection
    $inlinePlacement={inlinePlacement}
    data-testid={`${dataTestId}-main-section`}
  >
    {isTranslating && (
      <TranslatingOverlay
        role="dialog"
        aria-modal="true"
        aria-busy="true"
        aria-labelledby={`${dataTestId}-translate-overlay-title`}
        data-testid={`${dataTestId}-translate-loading-overlay`}
      >
        <TranslatingOverlayStack>
          <Spinner variant="secondary" size="xlarge" />
          <TranslatingOverlayCopy id={`${dataTestId}-translate-overlay-title`}>
            Translating questions for this language…
          </TranslatingOverlayCopy>
        </TranslatingOverlayStack>
      </TranslatingOverlay>
    )}
    <FormPreviewCard
      $inlinePlacement={inlinePlacement}
      data-testid={`${dataTestId}-card`}
    >
      {readOnly ? (
        <>
          {sortedQuestions.length === 0 ? (
            <QuestionContentSection $inlinePlacement={inlinePlacement}>
              <QuestionDescription $inlinePlacement={inlinePlacement}>
                No questions in this language.
              </QuestionDescription>
            </QuestionContentSection>
          ) : (
            <>
              <QuestionContentSection $inlinePlacement={inlinePlacement}>
                <QuestionContentInner $inlinePlacement={inlinePlacement}>
                  <QuestionTextSection $inlinePlacement={inlinePlacement}>
                    <QuestionTitle
                      $inlinePlacement={inlinePlacement}
                      data-testid={`${dataTestId}-question-text`}
                    >
                      {currentQuestionReadOnly?.questionText ||
                        `Question ${
                          currentQuestionReadOnly?.displayNumber ??
                          readOnlyStep + 1
                        }`}
                      {currentQuestionReadOnly?.isRequired ? ' *' : ''}
                    </QuestionTitle>
                    {currentQuestionReadOnly?.description && (
                      <QuestionDescription
                        $inlinePlacement={inlinePlacement}
                        $isRichText={hasRichTextFormatting(
                          currentQuestionReadOnly.description,
                        )}
                        data-testid={`${dataTestId}-question-description`}
                      >
                        <PreviewRichText
                          value={currentQuestionReadOnly.description}
                        />
                      </QuestionDescription>
                    )}
                  </QuestionTextSection>
                </QuestionContentInner>
              </QuestionContentSection>
              <ProgressSection data-testid={`${dataTestId}-progress-section`}>
                <ProgressText data-testid={`${dataTestId}-progress-text`}>
                  {sortedQuestions.length === 0 ? 0 : readOnlyStep + 1}/
                  {sortedQuestions.length} questions
                </ProgressText>
                <ProgressBarRow>
                  <ProgressBarWrapper>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(progressReadOnly)}
                      data-testid={`${dataTestId}-progress-bar`}
                      sx={{ width: '100%' }}
                    />
                  </ProgressBarWrapper>
                  <ButtonWrapper>
                    <NavButton
                      $inlinePlacement={inlinePlacement}
                      onClick={handleBackReadOnly}
                      data-testid={`${dataTestId}-previous-button`}
                      disabled={!showBackButton}
                    >
                      Previous
                    </NavButton>
                    <Button
                      variant="primary"
                      size="medium"
                      onClick={handleNextReadOnly}
                      data-testid={`${dataTestId}-next-button`}
                      disabled={!showNextButton}
                    >
                      Next
                    </Button>
                  </ButtonWrapper>
                </ProgressBarRow>
              </ProgressSection>
            </>
          )}
        </>
      ) : (
        <>
          {!hasQuestions ? (
            <EmptyStateContainer data-testid={`${dataTestId}-empty-state`}>
              <EmptyStateImage>
                <img src={emptyStateSrc} alt="" />
              </EmptyStateImage>
              <EmptyStateText>No questions to preview</EmptyStateText>
            </EmptyStateContainer>
          ) : (
            <>
              {screen === PREVIEW_SCREENS.QUESTIONS &&
                currentQuestionInteractive && (
                  <QuestionContentSection $inlinePlacement={inlinePlacement}>
                    <QuestionContentInner $inlinePlacement={inlinePlacement}>
                      <QuestionTextSection $inlinePlacement={inlinePlacement}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <QuestionTitle
                              $inlinePlacement={inlinePlacement}
                              data-testid={`${dataTestId}-question-text`}
                            >
                              {currentQuestionInteractive.questionText ||
                                'Question text'}
                              {currentQuestionInteractive.isRequired
                                ? ' (Required)'
                                : ''}
                            </QuestionTitle>
                            {currentQuestionInteractive.description && (
                              <QuestionDescription
                                $inlinePlacement={inlinePlacement}
                                $isRichText={true}
                                data-testid={`${dataTestId}-question-description`}
                              >
                                <PreviewRichText
                                  value={currentQuestionInteractive.description}
                                />
                              </QuestionDescription>
                            )}
                          </Box>
                          {currentQuestionInteractive.questionType ===
                            ANSWER_TYPES.RADIO &&
                            answers[currentQuestionInteractive.id]
                              ?.radioOption && (
                              <Box
                                component="button"
                                onClick={() => {
                                  dispatch({
                                    type: 'SET_RADIO',
                                    questionId: currentQuestionInteractive.id,
                                    payload: null,
                                  });
                                  dispatch({
                                    type: 'SET_OTHER_TEXT',
                                    questionId: currentQuestionInteractive.id,
                                    payload: '',
                                  });
                                }}
                                data-testid={`${dataTestId}-clear-selection`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 16px',
                                  border: '1px solid #BDBDBD',
                                  borderRadius: '8px',
                                  background: 'white',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  color: '#000',
                                  whiteSpace: 'nowrap',
                                  '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: '16px' }} />
                                Clear selection
                              </Box>
                            )}
                        </Box>
                      </QuestionTextSection>
                      <QuestionInput
                        currentQuestion={currentQuestionInteractive}
                        answers={answers}
                        dispatch={dispatch}
                        handleFileSelect={handlers.handleFileSelect}
                        handleDeleteFile={handlers.handleDeleteFile}
                        handleFileDrop={handlers.handleFileDrop}
                        fileInputId={fileInputId}
                        inlinePlacement={inlinePlacement}
                        textInputPlaceholder={textInputPlaceholder}
                        dropdownPlaceholder={dropdownPlaceholder}
                        databaseFieldLabel={databaseFieldLabel}
                        databaseFieldPlaceholder={databaseFieldPlaceholder}
                      />
                    </QuestionContentInner>
                  </QuestionContentSection>
                )}

              {screen === PREVIEW_SCREENS.QUESTIONS && (
                <ProgressSection data-testid={`${dataTestId}-progress-section`}>
                  <ProgressText data-testid={`${dataTestId}-progress-text`}>
                    {currentQuestionPositionInteractive}/
                    {questionsCountInteractive} questions
                  </ProgressText>
                  <ProgressBarRow>
                    <ProgressBarWrapper>
                      <LinearProgress
                        variant="determinate"
                        value={Math.round(progressInteractive)}
                        data-testid={`${dataTestId}-progress-bar`}
                        sx={{ width: '100%' }}
                      />
                    </ProgressBarWrapper>
                    <ButtonWrapper>
                      <NavButton
                        $inlinePlacement={inlinePlacement}
                        onClick={handlers.handleBack}
                        data-testid={`${dataTestId}-previous-button`}
                        disabled={!showBackButton}
                      >
                        Previous
                      </NavButton>
                      <Button
                        variant="primary"
                        size="medium"
                        onClick={handlers.handleNext}
                        data-testid={`${dataTestId}-next-button`}
                        disabled={!showNextButton}
                      >
                        Next
                      </Button>
                    </ButtonWrapper>
                  </ProgressBarRow>
                </ProgressSection>
              )}
            </>
          )}
        </>
      )}
    </FormPreviewCard>
  </PreviewMainSection>
);
